/**
 * Progress content for member-facing outreach attempts.
 * Captures contact method, outcome, and notes. Outcome determines snooze behavior.
 * @agentic connections
 */

import {
  Stack,
  Text,
  Textarea,
  Button,
  Alert,
  Box,
  Group,
} from "@mantine/core";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { IconAlertCircle } from "@tabler/icons-react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { COMMUNICATION_MEDIUM_SELECT_OPTIONS } from "@benchmark/core/constants";
import {
  OUTREACH_STANDARD_OUTCOMES,
  OUTREACH_ENCOUNTER_OUTCOMES,
  ENCOUNTER_OUTCOME_ACTIVITY_CODES,
} from "@benchmark/core/constants";
import type { ProgressContentProps } from "@/types/domain/progress-log";
import type { ProgressContactMethod } from "@benchmark/core/types";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { toDateInputValue } from "@benchmark/ui/utils";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";

/**
 * Outreach attempt progress form for member-facing contact attempts.
 * @agentic connections
 */
export const OutreachAttemptProgressContent = ({
  task,
  onSubmit,
  onCancel,
  onDirtyChange,
}: ProgressContentProps) => {
  const [contactMethod, setContactMethod] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState<string>("");
  const [selectedSnoozePreset, setSelectedSnoozePreset] = useState<
    string | null
  >(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);

  const activityCode = task.code?.coding?.[0]?.code;
  const isEncounterActivity = activityCode
    ? ENCOUNTER_OUTCOME_ACTIVITY_CODES.has(activityCode)
    : false;

  const outcomeDefinitions = isEncounterActivity
    ? OUTREACH_ENCOUNTER_OUTCOMES
    : OUTREACH_STANDARD_OUTCOMES;

  const outcomeOptions = useMemo(
    () => outcomeDefinitions.map((o) => ({ value: o.value, label: o.label })),
    [outcomeDefinitions],
  );

  const contactMethodOptions = useMemo(
    () =>
      COMMUNICATION_MEDIUM_SELECT_OPTIONS.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    [],
  );

  const requiresSnoozePrompt = useMemo(() => {
    return outcomes.some((o) => {
      const outcome = outcomeDefinitions.find((def) => def.value === o);
      return outcome?.snoozeBehavior.type === "prompt";
    });
  }, [outcomes, outcomeDefinitions]);

  const isDirty =
    !!contactMethod ||
    outcomes.length > 0 ||
    notes.trim() !== "" ||
    !!snoozeDate ||
    !!careMinutes;
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const applySnoozePreset = useCallback((days: number, label: string) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setSnoozeDate(toDateInputValue(date));
    setSelectedSnoozePreset(label);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!contactMethod || outcomes.length === 0) {
      setValidationError(true);
      return;
    }

    if (requiresSnoozePrompt && !snoozeDate) {
      setValidationError(true);
      return;
    }

    const selectedOutcomes = outcomes
      .map((o) => outcomeDefinitions.find((def) => def.value === o))
      .filter(Boolean);

    if (selectedOutcomes.length === 0) return;

    const primaryOutcome = selectedOutcomes[0]!;
    const promptOutcome = selectedOutcomes.find(
      (o) => o?.snoozeBehavior.type === "prompt",
    );
    const snoozeBehavior =
      promptOutcome?.snoozeBehavior ?? primaryOutcome.snoozeBehavior;

    const followUpDate =
      requiresSnoozePrompt && snoozeDate ? snoozeDate : undefined;

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        contactMethod: contactMethod as ProgressContactMethod,
        outcome: outcomes.join(","),
        outcomeDisplay: selectedOutcomes.map((o) => o?.label).join(", "),
        notes: notes || undefined,
        snoozeBehavior,
        followUpDate,
        minutesLogged: careMinutes && careMinutes > 0 ? careMinutes : undefined,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to log outreach attempt",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    contactMethod,
    outcomes,
    outcomeDefinitions,
    notes,
    requiresSnoozePrompt,
    snoozeDate,
    onSubmit,
    careMinutes,
  ]);

  const canSubmit =
    !!contactMethod &&
    outcomes.length > 0 &&
    (!requiresSnoozePrompt || !!snoozeDate);

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Text size="sm">Log an outreach attempt for this activity.</Text>

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Contact Method{" "}
          <Text span c="red">
            *
          </Text>
        </Text>
        <CardSelect
          data={contactMethodOptions}
          value={contactMethod}
          onChange={(value) => {
            const singleValue = Array.isArray(value) ? value[0] : value;
            setContactMethod(singleValue);
            setValidationError(false);
          }}
          columns={2}
          error={validationError && !contactMethod}
          aria-label="Select contact method"
        />
      </Box>

      <Box>
        <Text size="sm" fw={500} mb="xs">
          Outcome{" "}
          <Text span c="red">
            *
          </Text>
        </Text>
        <CardSelect
          data={outcomeOptions}
          value={outcomes}
          onChange={(value) => {
            const arrayValue = Array.isArray(value)
              ? value
              : value
                ? [value]
                : [];
            setOutcomes(arrayValue);
            setValidationError(false);
          }}
          multiple
          columns={2}
          error={validationError && outcomes.length === 0}
          aria-label="Select outcome"
        />
      </Box>

      {requiresSnoozePrompt && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Snooze Duration{" "}
            <Text span c="red">
              *
            </Text>
          </Text>
          <Group gap="sm" mb="sm">
            <Button
              variant={selectedSnoozePreset === "1d" ? "filled" : "light"}
              color={selectedSnoozePreset === "1d" ? "purple" : undefined}
              size="xs"
              onClick={() => applySnoozePreset(1, "1d")}
            >
              1 day
            </Button>
            <Button
              variant={selectedSnoozePreset === "3d" ? "filled" : "light"}
              color={selectedSnoozePreset === "3d" ? "purple" : undefined}
              size="xs"
              onClick={() => applySnoozePreset(3, "3d")}
            >
              3 days
            </Button>
            <Button
              variant={selectedSnoozePreset === "1w" ? "filled" : "light"}
              color={selectedSnoozePreset === "1w" ? "purple" : undefined}
              size="xs"
              onClick={() => applySnoozePreset(7, "1w")}
            >
              Next week
            </Button>
          </Group>
          <TextInput
            type="date"
            label="Or pick a custom date"
            value={snoozeDate}
            onChange={(e) => {
              setSnoozeDate(e.currentTarget.value);
              setSelectedSnoozePreset(null);
            }}
            min={toDateInputValue(new Date())}
            error={
              validationError && requiresSnoozePrompt && !snoozeDate
                ? "Snooze date is required"
                : undefined
            }
          />
        </Box>
      )}

      <Textarea
        label="Notes (optional)"
        placeholder="Add details about the interaction..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        autosize
        minRows={2}
        maxRows={4}
      />

      <CareTimeActivityDurationField
        minutes={careMinutes}
        onMinutesChange={setCareMinutes}
      />

      <Stack gap="xs">
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
          color="purple"
          fullWidth
        >
          Log Outreach Attempt
        </Button>
        <Button
          variant="subtle"
          onClick={onCancel}
          disabled={submitting}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
